import { supabase } from '../../lib/supabase'
import { validateFile, sanitizeFilename } from '../../lib/validators'
import { getCurrentUserId, verifyPropertyWriteAccess, verifyPropertyAccess, verifyTenancyWriteAccess } from '../../lib/auth'
import { logTenancyCreated } from '../../lib/audit'
import { applyPropertyFilter } from '../../lib/portfolioFilter'
import type {
  Tenancy,
  TenancyInsert,
  TenancyUpdate
} from '../../types/tenancy'

// ============================================================
// TENANCIES
// ============================================================

export async function getTenancies(propertyId?: string, propertyIds?: string[], userIdOverride?: string): Promise<Tenancy[]> {
  let query = supabase
    .from('tenancies')
    .select(`
      *,
      property:properties!inner(*),
      unit:units(*),
      tenants:tenancy_tenants(
        *,
        tenant:profiles!tenancy_tenants_tenant_id_fkey(id, full_name, email, phone)
      ),
      tenant_invites(id, full_name, email, phone, status)
    `)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })

  // Dashboard agent-client view passes userIdOverride to scope to a
  // specific landlord's properties. Normal list relies on RLS (owner
  // mig 130 + team-read mig 282) — no client filter needed.
  if (userIdOverride) {
    query = query.eq('property.owner_id', userIdOverride)
  }

  query = applyPropertyFilter(query, propertyIds)
  if (propertyId) {
    query = query.eq('property_id', propertyId)
  }

  const { data, error } = await query

  if (error) throw error
  return data
}

export async function getTenancy(id: string): Promise<Tenancy> {
  const { data, error } = await supabase
    .from('tenancies')
    .select(`
      *,
      property:properties!inner(*),
      unit:units(*),
      tenants:tenancy_tenants(
        *,
        tenant:profiles(id, full_name, email, phone)
      ),
      tenant_invites(id, full_name, email, phone, status)
    `)
    .eq('id', id)
    .is('deleted_at', null)
    .single()

  if (error) throw error
  return data
}

export interface TenantTenancyInfo {
  tenancy: Tenancy
  skippedOnboarding: boolean
}

export async function getTenancyByTenant(tenantId: string): Promise<Tenancy | null> {
  const result = await getTenancyByTenantWithInfo(tenantId)
  return result?.tenancy || null
}

export async function getTenancyByTenantWithInfo(tenantId: string): Promise<TenantTenancyInfo | null> {
  const { data, error } = await supabase
    .from('tenancy_tenants')
    .select(`
      skipped_onboarding,
      tenancy:tenancies(
        *,
        property:properties(*),
        unit:units(*)
      )
    `)
    .eq('tenant_id', tenantId)
    .is('removed_at', null)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null // No rows found
    throw error
  }
  if (!data?.tenancy) return null

  const tenancy = data.tenancy as unknown as Tenancy

  return {
    tenancy,
    skippedOnboarding: data.skipped_onboarding || false
  }
}

export async function createTenancy(input: TenancyInsert): Promise<Tenancy> {
  // Verify the current user has write access to the property (owner, agent, or team writer)
  await verifyPropertyWriteAccess(input.property_id)

  const { data, error } = await supabase
    .from('tenancies')
    .insert(input)
    .select()
    .single()

  if (error) throw error

  // Audit log — append-only, non-blocking
  logTenancyCreated(data.id, {
    property_id: data.property_id,
    unit_id: data.unit_id,
    contract_type: data.contract_type,
    assigned_agent_id: data.assigned_agent_id,
  }).catch(() => {})

  // Sync property.rent_amount from active tenancies (non-blocking)
  syncPropertyRentFromTenancies(data.property_id).catch(() => {})

  return data
}

export async function updateTenancy(id: string, input: TenancyUpdate): Promise<Tenancy> {
  // Verify write access: fetch tenancy's property_id, then check property write access
  const { data: existing } = await supabase.from('tenancies').select('property_id').eq('id', id).is('deleted_at', null).single()
  if (!existing) throw new Error('Tenancy not found')
  await verifyPropertyWriteAccess(existing.property_id)

  const { data, error } = await supabase
    .from('tenancies')
    .update(input)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error

  // Sync rent amount to future unpaid rent payments
  if (input.rent_amount_pence != null) {
    try {
      await syncRentPaymentsWithTenancy(id, input.rent_amount_pence)
    } catch (syncError) {
      // Non-blocking: log but don't fail the tenancy update
      console.error('Failed to sync rent payments with tenancy update:', syncError)
    }
  }

  // Sync due dates when rent_due_day changes
  if (input.rent_due_day !== undefined) {
    try {
      await syncRentPaymentDueDates(id, input.rent_due_day)
    } catch (syncError) {
      console.error('Failed to sync rent payment due dates:', syncError)
    }
  }

  // Sync property.rent_amount from active tenancies when rent or status changes (non-blocking)
  if (input.rent_amount_pence !== undefined || input.status !== undefined) {
    syncPropertyRentFromTenancies(data.property_id).catch(() => {})
  }

  return data
}

/**
 * Sync future unpaid rent payments when tenancy rent amount changes.
 * Updates 'upcoming' and 'due' payments only (preserves historical accuracy).
 */
async function syncRentPaymentsWithTenancy(tenancyId: string, rentAmountPence: number): Promise<void> {
  const { error } = await supabase
    .from('rent_payments')
    .update({ amount_due_pence: rentAmountPence })
    .eq('tenancy_id', tenancyId)
    .in('status', ['upcoming', 'due'])

  if (error) {
    console.error('Error syncing rent payments:', error)
    throw error
  }
}

/**
 * Sync due dates on unpaid rent payments when rent_due_day changes.
 * Updates 'upcoming', 'due', and 'overdue' payments (preserves paid history).
 */
async function syncRentPaymentDueDates(tenancyId: string, newDueDay: number): Promise<void> {
  // Fetch all unpaid payments for this tenancy
  const { data: payments, error: fetchError } = await supabase
    .from('rent_payments')
    .select('id, due_date')
    .eq('tenancy_id', tenancyId)
    .in('status', ['upcoming', 'due', 'overdue'])

  if (fetchError) throw fetchError
  if (!payments || payments.length === 0) return

  // Update each payment's due_date to use the new day, keeping same month/year
  for (const payment of payments) {
    const d = new Date(payment.due_date)
    // Clamp day to max days in that month (e.g. 31 → 28 for Feb)
    const maxDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
    const clampedDay = Math.min(newDueDay, maxDay)
    const newDate = new Date(d.getFullYear(), d.getMonth(), clampedDay)
    const newDateStr = newDate.toISOString().split('T')[0]

    // Recalculate status based on new due date
    const today = new Date().toISOString().split('T')[0]
    let newStatus: 'upcoming' | 'due' | 'overdue' = 'upcoming'
    if (newDateStr < today) newStatus = 'overdue'
    else if (newDateStr === today) newStatus = 'due'

    await supabase
      .from('rent_payments')
      .update({ due_date: newDateStr, status: newStatus })
      .eq('id', payment.id)
  }
}

/**
 * Sync property.rent_amount from active tenancies.
 * Called on tenancy create/update/delete to keep property-level rent in sync.
 * For occupied properties: total monthly rent from all active tenancies.
 * For vacant properties: leaves rent_amount unchanged (used as marketing/asking rent).
 */
async function syncPropertyRentFromTenancies(propertyId: string): Promise<void> {
  const { data: activeTenancies, error: queryError } = await supabase
    .from('tenancies')
    .select('rent_amount_pence, rent_frequency')
    .eq('property_id', propertyId)
    .in('status', ['active', 'ending'])
    .is('deleted_at', null)

  if (queryError) {
    console.error('Failed to query tenancies for rent sync:', queryError)
    return
  }

  // Only sync if there are active tenancies — vacant property rent stays as-is
  if (!activeTenancies || activeTenancies.length === 0) return

  const totalMonthlyPence = activeTenancies.reduce((sum, t) => {
    if (t.rent_frequency === 'monthly') return sum + (t.rent_amount_pence || 0)
    if (t.rent_frequency === 'weekly') return sum + Math.round(((t.rent_amount_pence || 0) * 52) / 12)
    return sum
  }, 0)

  const { error: updateError } = await supabase
    .from('properties')
    .update({ rent_amount_pence: totalMonthlyPence })
    .eq('id', propertyId)

  if (updateError) {
    console.error('Failed to sync property rent:', updateError)
  }
}

export async function deleteTenancy(id: string): Promise<void> {
  // Get property_id before soft-delete for rent sync and verify ownership
  const { data: tenancy } = await supabase
    .from('tenancies')
    .select('property_id')
    .eq('id', id)
    .is('deleted_at', null)
    .single()

  if (!tenancy) throw new Error('Tenancy not found')
  await verifyPropertyWriteAccess(tenancy.property_id)

  // Find any prospect linked to this tenancy before soft-delete
  const { data: linkedProspect } = await supabase
    .from('prospects')
    .select('id')
    .eq('converted_tenancy_id', id)
    .is('deleted_at', null)
    .maybeSingle()

  // Soft-delete associated rent_payments (FK cascade only fires on hard delete)
  await supabase
    .from('rent_payments')
    .update({ deleted_at: new Date().toISOString() })
    .eq('tenancy_id', id)
    .is('deleted_at', null)

  const { error } = await supabase.rpc('soft_delete_record', {
    target_table: 'tenancies',
    record_id: id,
  })

  if (error) throw error

  // Revert prospect status so it recalculates based on remaining stages
  if (linkedProspect) {
    await supabase
      .from('prospects')
      .update({ status: 'lead', converted_at: null })
      .eq('id', linkedProspect.id)

    const { recalculateProspectStatus } = await import('../prospects/core')
    recalculateProspectStatus(linkedProspect.id).catch(() => {})
  }

  // Sync property.rent_amount after tenancy removal (non-blocking)
  if (tenancy?.property_id) {
    syncPropertyRentFromTenancies(tenancy.property_id).catch(() => {})
  }
}

export async function archiveTenancy(id: string): Promise<Tenancy> {
  return updateTenancy(id, { status: 'archived' })
}

/**
 * Manually activate a tenancy that is in 'onboarding' status.
 * Used as a landlord override when onboarding steps are incomplete.
 * Logs which steps were skipped to the audit trail.
 */
export async function activateTenancy(id: string): Promise<Tenancy> {
  const propertyId = await verifyTenancyWriteAccess(id)

  // Verify tenancy is in onboarding or moving_in status
  const { data: existing, error: fetchError } = await supabase
    .from('tenancies')
    .select('status, start_date')
    .eq('id', id)
    .is('deleted_at', null)
    .single()

  if (fetchError || !existing) throw new Error('Tenancy not found')
  if (existing.status !== 'onboarding' && existing.status !== 'moving_in') {
    throw new Error('Tenancy must be in onboarding or moving in status')
  }

  // Determine target status:
  // - From moving_in → always go to active (early move-in override)
  // - From onboarding with future start_date → moving_in
  // - From onboarding with start_date <= today → active
  const today = new Date().toISOString().split('T')[0]
  const isFutureStart = existing.status === 'onboarding' && existing.start_date > today
  const targetStatus = existing.status === 'moving_in' ? 'active' : (isFutureStart ? 'moving_in' : 'active')

  // Fetch incomplete onboarding steps for audit trail
  const { data: incompleteSteps } = await supabase
    .from('tenant_onboarding_steps')
    .select('step_key, label')
    .eq('tenancy_id', id)
    .is('completed_at', null)

  const updated = await updateTenancy(id, { status: targetStatus })

  // Audit trail for manual activation
  const userId = await getCurrentUserId()
  supabase.from('audit_log').insert({
    user_id: userId,
    action: targetStatus === 'active' ? 'tenancy_manually_activated' : 'tenancy_manually_moving_in',
    entity_type: 'tenancy',
    entity_id: id,
    new_data: {
      trigger: 'manual_override',
      previous_status: existing.status,
      target_status: targetStatus,
      start_date: existing.start_date,
      skipped_steps: incompleteSteps?.map(s => s.step_key) || [],
      skipped_step_labels: incompleteSteps?.map(s => s.label) || [],
    },
  }).then(() => {}).catch(() => {})

  // Generate rent schedule only when transitioning to active (non-blocking)
  if (targetStatus === 'active') {
    try {
      const { generateRentSchedule } = await import('../financials')
      await generateRentSchedule(id)
    } catch {
      // Non-critical — rent schedule can be regenerated later
    }

    // Sync property rent (non-blocking)
    syncPropertyRentFromTenancies(propertyId).catch(() => {})
  }

  return updated
}

// ============================================================
// TENANCY TERMINATION
// ============================================================

export async function terminateTenancy(
  tenancyId: string,
  params: {
    reason: 'tenant_notice' | 'landlord_section8' | 'mutual_agreement' | 'abandonment'
    noticeServedAt: string
    noticeEndDate: string
  }
): Promise<void> {
  const propertyId = await verifyTenancyWriteAccess(tenancyId)

  // Verify tenancy is in a terminable state
  const { data: existing } = await supabase
    .from('tenancies')
    .select('status')
    .eq('id', tenancyId)
    .is('deleted_at', null)
    .single()

  if (!existing) throw new Error('Tenancy not found')
  if (existing.status !== 'active' && existing.status !== 'ending') {
    throw new Error('Tenancy must be active or ending to terminate')
  }

  // 1. Update tenancy to ending status
  await updateTenancy(tenancyId, {
    status: 'ending',
    terminated_at: new Date().toISOString(),
    termination_reason: params.reason,
    notice_served_at: params.noticeServedAt,
    notice_end_date: params.noticeEndDate,
    end_date: params.noticeEndDate,
  })

  // 2. Cancel future rent payments beyond notice period via RPC
  const { data: cancelledCount } = await supabase.rpc('cancel_future_rent_payments', {
    p_tenancy_id: tenancyId,
    p_after_date: params.noticeEndDate,
  })

  // 3. Void Xero invoices for cancelled payments (fire-and-forget)
  let voidedCount = 0
  const { data: cancelledWithXero } = await supabase
    .from('rent_payments')
    .select('id')
    .eq('tenancy_id', tenancyId)
    .eq('status', 'cancelled')
    .not('xero_invoice_id', 'is', null)
    .is('deleted_at', null)

  if (cancelledWithXero && cancelledWithXero.length > 0) {
    voidedCount = cancelledWithXero.length
    for (const payment of cancelledWithXero) {
      supabase.functions.invoke('xero-sync-rent', {
        body: { action: 'void', rent_payment_id: payment.id },
      }).catch(() => {})
    }
  }

  // 4. Sync property rent (non-blocking)
  syncPropertyRentFromTenancies(propertyId).catch(() => {})

  // 5. Audit log
  try {
    const userId = await getCurrentUserId()
    await supabase.from('audit_log').insert({
      user_id: userId,
      action: 'tenancy_termination_initiated',
      entity_type: 'tenancy',
      entity_id: tenancyId,
      new_data: {
        reason: params.reason,
        notice_served_at: params.noticeServedAt,
        notice_end_date: params.noticeEndDate,
        cancelled_payments_count: cancelledCount ?? 0,
        voided_invoices_count: voidedCount,
      },
    })
  } catch { /* Non-blocking */ }
}

export async function completeTenancyTermination(tenancyId: string): Promise<void> {
  await verifyTenancyWriteAccess(tenancyId)

  const { data: existing } = await supabase
    .from('tenancies')
    .select('status, property_id')
    .eq('id', tenancyId)
    .is('deleted_at', null)
    .single()

  if (!existing) throw new Error('Tenancy not found')
  if (existing.status !== 'ending') {
    throw new Error('Tenancy must be in ending status to complete termination')
  }

  await updateTenancy(tenancyId, { status: 'ended' })

  // Sync property rent (non-blocking)
  syncPropertyRentFromTenancies(existing.property_id).catch(() => {})

  try {
    const userId = await getCurrentUserId()
    await supabase.from('audit_log').insert({
      user_id: userId,
      action: 'tenancy_terminated',
      entity_type: 'tenancy',
      entity_id: tenancyId,
      new_data: { previous_status: 'ending' },
    })
  } catch { /* Non-blocking */ }
}

// ============================================================
// FILE UPLOAD HELPERS
// ============================================================

export async function uploadTenantFile(
  tenancyId: string,
  file: File,
  folder: string = 'documents'
): Promise<string> {
  const validation = validateFile(file, { isDocument: true })
  if (!validation.valid) {
    throw new Error(validation.error)
  }

  const safeName = sanitizeFilename(file.name)
  const ext = safeName.split('.').pop() || 'pdf'
  const path = `tenants/${tenancyId}/${folder}/${Date.now()}-${crypto.randomUUID().slice(0, 12)}.${ext}`

  const { error: uploadError } = await supabase.storage
    .from('tenant-documents')
    .upload(path, file, { contentType: file.type })

  if (uploadError) throw uploadError

  // Return storage path (not a public URL) — signed URLs are generated at read time
  return path
}
